package com.devi3east.bashbuddy.cart.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.cart.domain.models.CartFeeBreakdown
import com.devi3east.bashbuddy.cart.domain.models.CartItem
import com.devi3east.bashbuddy.features.common.util.formatPrice

@Composable
fun CartOrganizationCard(
    orgName: String,
    items: List<CartItem>,
    fees: CartFeeBreakdown,
    expanded: Boolean,
    onExpandToggle: () -> Unit,
    onRemoveItem: (String) -> Unit,
    onUpdateQuantity: (String, Int) -> Unit,
    onSetQuantity: (String, Int) -> Unit,
    onSetShipping: (String, Boolean) -> Unit,
    onCheckoutOrg: (Boolean) -> Unit
) {
    val interaction = remember { MutableInteractionSource() }
    val cardShape = RoundedCornerShape(16.dp)

    var jurisdictionEligibilityConfirmed by rememberSaveable(orgName) {
        mutableStateOf(false)
    }

    val shippingFeeTotal = fees.shipping
    val baseFee = fees.base
    val serviceFee = fees.platformFee + fees.stripeFee
    val grandTotal = fees.total

    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp)
            .clip(cardShape)
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.1f),
                shape = cardShape
            )
            .clickable(
                interactionSource = interaction,
                indication = LocalIndication.current,       // we use indication() above so ripple respects shape
                onClick = onExpandToggle
            ),
        shape = cardShape,
        elevation = CardDefaults.elevatedCardElevation()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {

            // --- Header row with org name + toggle ---
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    orgName,
                    style = MaterialTheme.typography.titleLarge,
                    modifier = Modifier.weight(1f),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                Icon(
                    imageVector = if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = if (expanded) "Collapse" else "Expand"
                )
            }

            AnimatedVisibility(visible = expanded) {
                Column {
                    Spacer(modifier = Modifier.height(8.dp))

                    items.forEach { item ->
                        CartItemCard(
                            cartItem = item,
                            onDelete = onRemoveItem,
                            onUpdateQuantity = onUpdateQuantity,
                            onSetQuantity = onSetQuantity,
                            onSetShipping = onSetShipping
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    HorizontalDivider()

                    Spacer(modifier = Modifier.height(8.dp))

                    if (shippingFeeTotal > 0) {
                        FeeRow(
                            label = "Shipping Fee:",
                            amount = shippingFeeTotal
                        )

                        Spacer(modifier = Modifier.height(4.dp))
                    }

                    FeeRow(
                        label = "Ticket Cost:",
                        amount = baseFee
                    )

                    Spacer(modifier = Modifier.height(4.dp))

                    FeeRow(
                        label = "Service Fee:",
                        amount = serviceFee
                    )

                    Spacer(modifier = Modifier.height(4.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = "Subtotal:",
                            style = MaterialTheme.typography.bodyLarge
                        )

                        Text(
                            text = formatPrice(grandTotal),
                            style = MaterialTheme.typography.bodyLarge.copy(
                                fontWeight = FontWeight.Bold
                            )
                        )
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    HorizontalDivider(
                        color = MaterialTheme.colorScheme.outlineVariant
                    )

                    Spacer(modifier = Modifier.height(12.dp))

                    Text(
                        text = "Location & Eligibility",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface
                    )

                    Spacer(modifier = Modifier.height(6.dp))

                    Text(
                        text = "By purchasing a raffle ticket, you represent that you are physically " +
                                "located in a jurisdiction where participation in this raffle is lawful. " +
                                "It is your responsibility to ensure that purchasing and participating " +
                                "in this raffle complies with all applicable federal, state, and local " +
                                "laws where you are located at the time of purchase.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .clickable {
                                jurisdictionEligibilityConfirmed =
                                    !jurisdictionEligibilityConfirmed
                            }
                            .padding(
                                horizontal = 4.dp,
                                vertical = 4.dp
                            ),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Checkbox(
                            checked = jurisdictionEligibilityConfirmed,
                            onCheckedChange = null
                        )

                        Text(
                            text = "I certify that I am legally eligible to purchase raffle tickets " +
                                    "in my current location.",
                            modifier = Modifier
                                .weight(1f)
                                .padding(
                                    start = 4.dp,
                                    end = 4.dp
                                ),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Button(
                        onClick = {
                            onCheckoutOrg(jurisdictionEligibilityConfirmed)
                        },
                        enabled = jurisdictionEligibilityConfirmed,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text(
                            "Checkout with $orgName",
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.fillMaxWidth(),
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }
    }
}